import React from 'react';
import { Icon } from '../Icon';
import { INTEGRATION_CATALOG } from './catalog';
import type { CatalogEntry, IntegrationId, IntegrationIconVariant } from './types';

export interface IntegrationIconProps {
  /** ID da integração. Aceita IntegrationId conhecido ou string genérica (fallback). */
  id: IntegrationId | string;
  /** Tamanho do ícone em px. Default 24. */
  size?: number;
  /** Variante visual. Default `brand`. */
  variant?: IntegrationIconVariant;
  /** Classes extras aplicadas no wrapper externo. */
  className?: string;
}

const FALLBACK: CatalogEntry = INTEGRATION_CATALOG['email-smtp'];

export const IntegrationIcon: React.FC<IntegrationIconProps> = ({
  id,
  size = 24,
  variant = 'brand',
  className,
}) => {
  const entry: CatalogEntry =
    (INTEGRATION_CATALOG as Record<string, CatalogEntry>)[id] ?? FALLBACK;

  const color = variant === 'mono' ? 'var(--theme-muted-text)' : entry.brandColor;

  const iconNode =
    entry.kind === 'simple-icon'
      ? <entry.icon size={size} color={color} />
      : <Icon name={entry.iconName} size={size} style={{ color }} />;

  if (variant === 'brand-soft') {
    const padding = Math.round(size * 0.3);
    return (
      <div
        className={className}
        role="img"
        aria-label={entry.label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding,
          borderRadius: 12,
          backgroundColor: `${entry.brandColor}1A`,
        }}
      >
        {iconNode}
      </div>
    );
  }

  return (
    <span className={className} role="img" aria-label={entry.label}>
      {iconNode}
    </span>
  );
};

IntegrationIcon.displayName = 'IntegrationIcon';

/**
 * Rótulo humano da integração, do catálogo central. Existe para que rótulo de
 * canal não seja escrito à mão em tela nenhuma (mesma regra que já vale para o
 * ícone). Sem correspondência no catálogo, devolve `undefined` — o chamador
 * decide o que mostrar.
 */
export const getIntegrationLabel = (id: IntegrationId | string): string | undefined =>
  (INTEGRATION_CATALOG as Record<string, CatalogEntry | undefined>)[id]?.label;
